import { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Button from "components/particles/primary-button";
import img from "assets/images/input-img-icon.svg";
import closeImage from "assets/images/input-img-close-icon.svg";
import { Container, StyledReactModal } from "./style";
import { useForm } from "react-hook-form";
import { BannerDTO } from "utils/helpers/models/banner.dto";
import FormErrorMessage from "components/particles/forms/form-error-message";
import { useBanners } from "containers/banners/useHook";
import { dispatch, RootState } from "store";
import { setModalClosed } from "store/user-slice";
import { useSelector } from "react-redux";
import Spinner from "components/particles/loaders/spinner";

interface AddBannersModelProps {
  data: any;
  setBanners: Function;
  banners: any[];
}

const AddBannersPopup: FC<AddBannersModelProps> = ({
  data,
  setBanners,
  banners,
}) => {
  const {
    handleSubmit,
    setValue,
    register,
    reset,
    formState: { errors },
    getValues,
  } = useForm<BannerDTO>();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { addBannerModalIsOpen, isLoading } = useSelector(
    (state: RootState) => state.user
  );
  const { postBanner, putBanner, getBanners } = useBanners();

  // handle image change and register file
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setValue("image", file, { shouldValidate: true });
    }
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      fontSize: "2rem",
      width: "68.5rem",
      borderRadius: "3rem",
    },
  };

  const closeModal = () => dispatch(setModalClosed("Banner"));
  const clearImage = () => {
    setSelectedImage(null);
    setValue("image", undefined, { shouldValidate: true });
  };

  useEffect(() => {
    if (data?.id) {
      // prefill form for edit
      Object.entries(data).forEach(([key, value]) => {
        if (key === "image") {
          setSelectedImage(value as string);
        }
        setValue(key as keyof BannerDTO, value);
      });
    } else {
      reset();
      setSelectedImage(null);
    }
  }, [data, reset, setValue]);

  const onSubmit = async (formDataDto: BannerDTO) => {
    const formData = new FormData();
    Object.entries(getValues()).forEach(([k, v]) =>
      formData.append(k, v as Blob | string)
    );

    if (formDataDto.id) {
      await putBanner(data.id, formData, banners, setBanners, data);
    } else {
      await postBanner(formData);
    }
    await getBanners(setBanners);
    closeModal();
  };

  return (
    <StyledReactModal
      isOpen={addBannerModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Add Banner Modal"
    >
      <Container onSubmit={handleSubmit(onSubmit)}>
        <button className="close-button" onClick={closeModal}>
          <img src={close} alt="Close" />
        </button>

        <div className="model-container">
          <div className="popup-title">
            <h2>{data?.id ? "Update Banner" : "Add Banner"}</h2>
          </div>

          <div className="add-ingredient-container">
            {/* IMAGE UPLOAD */}
            <div className="image-container">
              <div className="image-input-wrapper">
                {selectedImage ? (
                  <>
                    <img
                      className="uploaded-image"
                      src={selectedImage}
                      alt="Selected"
                    />
                    <img
                      className="close-icon"
                      src={closeImage}
                      alt="Remove"
                      onClick={clearImage}
                    />
                  </>
                ) : (
                  <>
                    <label htmlFor="image-input">
                      <img src={img} alt="Upload" />
                    </label>
                    <input
                      id="image-input"
                      type="file"
                      accept="image/*"
                      className="hidden-input"
                      {...register("image", { required: "Image is required" })}
                      onChange={handleImageChange}
                    />
                  </>
                )}
              </div>
              <FormErrorMessage error={errors.image} />
            </div>

            {/* HEADING */}
            <div>
              <input
                type="text"
                placeholder="Banner Heading"
                {...register("heading", {
                  required: "Heading is required",
                })}
              />
              <FormErrorMessage error={errors.heading} />
            </div>

            {/* DESCRIPTION */}
            <div>
              <input
                type="text"
                placeholder="Banner Description"
                {...register("description", {
                  required: "Description is required",
                })}
              />
              <FormErrorMessage error={errors.description} />
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <div className="add-button">
            <Button
              title={isLoading ? <Spinner /> : data?.id ? "Update" : "Add"}
              width="51.2rem"
              backgroundcolor="var(--primary)"
              fill="true"
            />
          </div>
        </div>
      </Container>
    </StyledReactModal>
  );
};

export default AddBannersPopup;
